/**
 * TopParent waitlist form.
 *
 * Pure HTML form intentionally rendered without any onSubmit handler,
 * action attribute, or fetch wiring. A campaign script loaded once in
 * public/index.html detects this form by id and binds the submit.
 * No backend, no other form provider, no redirect of our own.
 *
 * The form id (`topparent-waitlist-form`) and the two field names
 * (name, email) must stay exactly as below for KickoffLabs AnyForm.
 */

import { useEffect, useState } from "react";
import { trackEvent, identifyPerson } from "../lib/analytics";
import { UTM_KEYS, getUtms } from "../lib/utm";

const labelCls =
    "block text-[13px] font-semibold uppercase tracking-[0.08em] text-brand-navy/80 mb-1.5";
const inputCls =
    "w-full h-14 rounded-2xl bg-white border-2 border-brand-navy/15 px-5 text-[16px] text-brand-navy placeholder:text-[#94A3B8] shadow-sm hover:border-brand-navy/25 focus:outline-none focus:ring-4 focus:ring-brand-coral/15 focus:border-brand-coral transition-all";

const WaitlistForm = () => {
    // Read first-touch UTMs from sessionStorage (captured at app boot in
    // Layout.jsx). getUtms() is idempotent and will lazily capture from
    // the URL if storage is empty, so we can safely read it on first
    // render. Lazy initializer ensures sessionStorage is only touched
    // once per mount.
    const [utms] = useState(() => {
        if (typeof window === "undefined") {
            return UTM_KEYS.reduce((acc, k) => ({ ...acc, [k]: "" }), {});
        }
        return getUtms();
    });

    // Native submit listener fires GA4 + PostHog events WITHOUT preventing
    // the kol.js AnyForm script from handling the actual submission. We
    // intentionally do not use React's onSubmit so kol.js remains the sole
    // submit handler.
    useEffect(() => {
        const form = document.getElementById("topparent-waitlist-form");
        if (!form) return;
        const handler = () => {
            const email = form.elements?.email?.value || "";
            const name = form.elements?.name?.value || "";
            const utmPayload = UTM_KEYS.reduce((acc, k) => {
                acc[k] = form.elements?.[k]?.value || "";
                return acc;
            }, {});
            trackEvent("waitlist_signup", {
                form_id: "topparent-waitlist-form",
                ...utmPayload,
            });
            // Attach a stable identity in PostHog so this visitor's pre- and
            // post-signup events land on the same person profile. We use the
            // email as the distinct id and store name + first-touch UTMs as
            // person properties.
            if (email) {
                identifyPerson(email, {
                    name,
                    email,
                    ...utmPayload,
                });
            }
        };
        form.addEventListener("submit", handler);
        return () => form.removeEventListener("submit", handler);
    }, []);

    return (
        <form
            id="topparent-waitlist-form"
            data-testid="waitlist-form"
            className="space-y-4"
            noValidate
        >
            {/* Name */}
            <div>
                <label htmlFor="tp-waitlist-name" className={labelCls}>
                    Your name <span className="text-brand-coral">*</span>
                </label>
                <input
                    id="tp-waitlist-name"
                    data-testid="waitlist-input-name"
                    type="text"
                    name="name"
                    placeholder="e.g. Priya"
                    required
                    autoComplete="name"
                    className={inputCls}
                />
            </div>

            {/* Email */}
            <div>
                <label htmlFor="tp-waitlist-email" className={labelCls}>
                    Your email <span className="text-brand-coral">*</span>
                </label>
                <input
                    id="tp-waitlist-email"
                    data-testid="waitlist-input-email"
                    type="email"
                    name="email"
                    placeholder="you@example.com"
                    required
                    autoComplete="email"
                    inputMode="email"
                    className={inputCls}
                />
            </div>

            <button
                type="submit"
                data-testid="cta_founder_waitlist"
                className="btn-primary w-full h-14 text-[16px] justify-center mt-2 shadow-lg shadow-brand-coral/20 hover:shadow-xl hover:shadow-brand-coral/30 transition-shadow"
            >
                Join the waitlist
            </button>

            {/* Hidden UTM fields. Populated from sessionStorage first-touch
                attribution (see lib/utm.js + Layout.jsx). KickoffLabs AnyForm
                picks these up as lead custom fields so they appear in lead
                exports and downstream integrations. */}
            {UTM_KEYS.map((key) => (
                <input
                    key={key}
                    type="hidden"
                    name={key}
                    value={utms[key] || ""}
                    data-testid={`waitlist-input-${key}`}
                    readOnly
                />
            ))}
        </form>
    );
};

export default WaitlistForm;
